import { useEffect } from "react";
import "../styles/home.css";
import Header from "../components/Header/Header";
import Footer from "../components/Footer/Footer";
import { initScrollReveal } from "../utils/scrollReveal";

function Home() {
  useEffect(() => {
    initScrollReveal({
      once: true,          // animation runs only once
      threshold: 0.15,     // trigger earlier, smoother
      stagger: true        // allow staggered children
    });
  }, []);

  return (
    <>
      <Header />

      {/* ======================================================
         FULL-WIDTH PAGE SHELL
         ====================================================== */}
      <main className="home-shell">

        {/* ======================================================
           HERO SECTION (WIDE, TWO-COLUMN)
           ====================================================== */}
        <section
          className="hero-section reveal"
          style={{ transitionDelay: "0ms" }}
        >
          <div className="hero-container">

            {/* ---------- LEFT: TEXT ---------- */}
            <div
              className="hero-text reveal"
              style={{ transitionDelay: "120ms" }}
            >
              <h1
                className="hero-title reveal"
                style={{ transitionDelay: "180ms" }}
              >
                Structured startup credibility
                <br />
                for serious decision-makers
              </h1>

              <p
                className="hero-lead reveal"
                style={{ transitionDelay: "260ms" }}
              >
                Prufyy helps founders present their startup with clarity and
                helps investors, incubators, accelerators, angels, and venture
                capital firms evaluate startups consistently using structured
                claims and evidence.
              </p>

              <p
                className="hero-sub reveal"
                style={{ transitionDelay: "320ms" }}
              >
                It does not replace pitch decks or judgment. It introduces
                structure between what is claimed and what is verified.
              </p>

              <div
                className="hero-cta reveal"
                style={{ transitionDelay: "380ms" }}
              >
                <a href="/signup" className="btn-primary">
                  Create your Prufyy profile
                </a>
                <a href="/pricing" className="btn-secondary">
                  View pricing
                </a>
              </div>
            </div>

            {/* ---------- RIGHT: VISUAL CARD ---------- */}
            <div
              className="hero-visual reveal"
              style={{ transitionDelay: "220ms" }}
            >
              <div className="hero-mock-card">
                <div className="mock-header">
                  from messy pitch to clear startup profile
                </div>

                <div className="snapshot-compare">
                  <div className="snapshot-col before">
                    <div className="snapshot-title">before prufyy</div>
                    <ul>
                      <li><span className="icon bad">✕</span> pitch deck only</li>
                      <li><span className="icon bad">✕</span> claims mixed with opinions</li>
                      <li><span className="icon bad">✕</span> documents scattered in links</li>
                      <li><span className="icon bad">✕</span> same questions in every meeting</li>
                    </ul>
                  </div>

                  <div className="snapshot-col after">
                    <div className="snapshot-title">with prufyy</div>
                    <ul>
                      <li><span className="icon good">✓</span> pitch analyzed</li>
                      <li><span className="icon good">✓</span> claims clearly structured</li>
                      <li><span className="icon good">✓</span> evidence attached where needed</li>
                      <li><span className="icon good">✓</span> one profile shared with reviewers</li>
                    </ul>
                  </div>
                </div>

                <div className="mock-footer">
                  reviewers see clarity, not confusion
                </div>
              </div>
            </div>

          </div>
        </section>
        {/* END HERO SECTION */}
        {/* ======================================================
           CONTENT CONTAINER STARTS HERE
           ====================================================== */}
        <div className="home-container">

        {/* ======================================================
           PROBLEM SECTION (OFFSET LEFT)
           ====================================================== */}
        <section
          className="section-block offset-left reveal"
          style={{ transitionDelay: "0ms" }}
        >
          <div
            className="section-card reveal"
            style={{ transitionDelay: "120ms" }}
          >
            <h2
              className="section-title reveal"
              style={{ transitionDelay: "180ms" }}
            >
              Fundraising is not the problem.<br />
              Credibility translation is.
            </h2>

            <p
              className="reveal"
              style={{ transitionDelay: "260ms" }}
            >
              Founders are not lacking conviction, effort, or ambition.
              Evaluators are not lacking experience or judgment. What breaks
              down is how startup claims are interpreted, questioned, and
              verified across different decision-makers and institutions.
            </p>

            <p
              className="reveal"
              style={{ transitionDelay: "320ms" }}
            >
              Every pitch compresses months or years of work into a narrative.
              That narrative is then interpreted through different lenses —
              angels, analysts, partners, mentors, committees — each asking
              different questions about the same underlying claims.
            </p>
          </div>
        </section>

        {/* ======================================================
           CONSEQUENCES (OFFSET RIGHT, LIGHT BG)
           ====================================================== */}
        <section
          className="section-block offset-right alt-bg reveal"
          style={{ transitionDelay: "0ms" }}
        >
          <div
            className="section-card reveal"
            style={{ transitionDelay: "120ms" }}
          >
            <h3
              className="section-subtitle reveal"
              style={{ transitionDelay: "180ms" }}
            >
              What this creates in practice
            </h3>

            <ul
              className="bullet-list reveal"
              style={{ transitionDelay: "260ms" }}
            >
              <li>Repeated explanations across meetings</li>
              <li>Documents shared reactively instead of intentionally</li>
              <li>Credibility inferred instead of inspected</li>
              <li>Decisions shaped by interpretation, not evidence</li>
            </ul>
          </div>
        </section>

        {/* ======================================================
           STRUCTURAL FAILURE OF PITCH DECKS
           ====================================================== */}
        <section
          className="section-block offset-left reveal"
          style={{ transitionDelay: "0ms" }}
        >
          <div
            className="section-card reveal"
            style={{ transitionDelay: "120ms" }}
          >
            <h2
              className="section-title reveal"
              style={{ transitionDelay: "180ms" }}
            >
              Why pitch decks fail structurally
            </h2>

            <div
              className="card-grid two-col reveal"
              style={{ transitionDelay: "260ms" }}
            >
              <div className="info-card">
                <h4>Designed to persuade</h4>
                <p>
                  Pitch decks are optimized for storytelling and momentum —
                  not for isolating, inspecting, or verifying claims.
                </p>
              </div>

              <div className="info-card">
                <h4>Claims get diluted</h4>
                <p>
                  Strong claims sit beside weaker ones. Important assertions
                  disappear inside narrative flow.
                </p>
              </div>

              <div className="info-card">
                <h4>Evidence lives elsewhere</h4>
                <p>
                  Proof is scattered across folders, links, emails, and
                  follow-ups — detached from the claim itself.
                </p>
              </div>

              <div className="info-card">
                <h4>Inference replaces inspection</h4>
                <p>
                  Evaluators infer what supports a claim instead of inspecting
                  it directly, making outcomes structurally unreliable.
                </p>
              </div>
            </div>
          </div>
        </section>
        {/* END PITCH DECK FAILURE SECTION */}
        {/* ======================================================
           WHAT PRUFYY INTRODUCES (STRUCTURED CARDS)
           ====================================================== */}
        <section
          className="section-block offset-right reveal"
          style={{ transitionDelay: "0ms" }}
        >
          <div
            className="section-card reveal"
            style={{ transitionDelay: "120ms" }}
          >
            <h2
              className="section-title reveal"
              style={{ transitionDelay: "180ms" }}
            >
              What Prufyy introduces
            </h2>

            <div
              className="card-grid three-col reveal"
              style={{ transitionDelay: "260ms" }}
            >
              <div className="info-card">
                <h4>Claim separation</h4>
                <p>
                  Pitch statements are separated from narrative flow and grouped
                  into explicit, reviewable claims — so important assertions
                  don’t get buried.
                </p>
              </div>

              <div className="info-card">
                <h4>Evidence in context</h4>
                <p>
                  Documents, data, and references are attached directly where
                  credibility matters, instead of being scattered across links.
                </p>
              </div>

              <div className="info-card">
                <h4>Narrative preserved</h4>
                <p>
                  Your pitch remains your pitch. Prufyy adds structure alongside
                  the story — it does not rewrite it.
                </p>
              </div>

              <div className="info-card">
                <h4>Human judgment protected</h4>
                <p>
                  No scoring, no rankings, no AI verdicts. Evaluation remains
                  human, contextual, and independent.
                </p>
              </div>

              <div className="info-card">
                <h4>Control by design</h4>
                <p>
                  Founders decide what is shared, with whom, and for how long.
                  Nothing is public by default.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* ======================================================
           CLAIM → EVIDENCE (VISUAL BLOCK)
           ====================================================== */}
        <section
          className="section-block offset-left alt-bg reveal"
          style={{ transitionDelay: "0ms" }}
        >
          <div
            className="section-card wide reveal"
            style={{ transitionDelay: "120ms" }}
          >
            <h3
              className="section-subtitle reveal"
              style={{ transitionDelay: "180ms" }}
            >
              A founder claim, done right
            </h3>

            <div
              className="claim-evidence-block reveal"
              style={{ transitionDelay: "260ms" }}
            >
              <div className="claim-side">
                <span className="label">Claim</span>
                <p>
                  “We have strong early enterprise traction.”
                </p>
              </div>

              <div className="evidence-side">
                <span className="label">Evidence</span>
                <ul>
                  <li>Signed letters of intent</li>
                  <li>Early revenue statements</li>
                  <li>Customer reference calls</li>
                </ul>
              </div>
            </div>

            <p
              className="muted reveal"
              style={{ transitionDelay: "340ms" }}
            >
              Evaluators no longer infer what supports the claim. They inspect it
              directly — in context.
            </p>
          </div>
        </section>
        {/* END CLAIM → EVIDENCE SECTION */}
        {/* ======================================================
           FOUNDER PERSPECTIVE (WITH CTA)
           ====================================================== */}
        <section
          className="section-block offset-right reveal"
          style={{ transitionDelay: "0ms" }}
        >
          <div
            className="section-card reveal"
            style={{ transitionDelay: "120ms" }}
          >
            <span
              className="eyebrow reveal"
              style={{ transitionDelay: "160ms" }}
            >
              Founder perspective
            </span>

            <h2
              className="section-title reveal"
              style={{ transitionDelay: "200ms" }}
            >
              Present clarity instead of hype
            </h2>

            <p className="reveal" style={{ transitionDelay: "260ms" }}>
              Founders lose credibility not because they exaggerate, but because
              their strongest claims are buried inside storytelling, repeated
              across conversations, and questioned inconsistently.
            </p>

            <ul
              className="bullet-list reveal"
              style={{ transitionDelay: "320ms" }}
            >
              <li>Important claims get lost inside narrative</li>
              <li>Proof lives across folders and follow-ups</li>
              <li>The same diligence questions repeat endlessly</li>
              <li>Feedback feels vague or contradictory</li>
            </ul>

            <div
              className="section-cta reveal"
              style={{ transitionDelay: "380ms" }}
            >
              <a href="/solutions/founder" className="cta-link">
                Explore Prufyy for Founders →
              </a>
            </div>
          </div>
        </section>

        {/* ======================================================
           INCUBATOR PERSPECTIVE
           ====================================================== */}
        <section
          className="section-block offset-left alt-bg reveal"
          style={{ transitionDelay: "0ms" }}
        >
          <div
            className="section-card wide reveal"
            style={{ transitionDelay: "120ms" }}
          >
            <span
              className="eyebrow reveal"
              style={{ transitionDelay: "160ms" }}
            >
              Incubator perspective
            </span>

            <h2
              className="section-title reveal"
              style={{ transitionDelay: "200ms" }}
            >
              Incubation is a credibility responsibility,
              <br />
              not just founder support
            </h2>

            <p className="reveal" style={{ transitionDelay: "260ms" }}>
              Incubators do more than mentor startups. They implicitly shape how
              early-stage ventures are trusted beyond the program. Every cohort
              compounds institutional reputation.
            </p>

            <div
              className="card-grid two-col reveal"
              style={{ transitionDelay: "320ms" }}
            >
              <div className="info-card">
                <h4>Inconsistent evaluation</h4>
                <p>
                  Mentor feedback varies widely across programs, making selection
                  logic difficult to defend later.
                </p>
              </div>

              <div className="info-card">
                <h4>Lost institutional memory</h4>
                <p>
                  Insights disappear after each cohort. Decisions live only in
                  people, not systems.
                </p>
              </div>

              <div className="info-card">
                <h4>Narrative-heavy intake</h4>
                <p>
                  Applications rely heavily on storytelling with limited claim
                  verification.
                </p>
              </div>

              <div className="info-card">
                <h4>Weak defensibility</h4>
                <p>
                  Past decisions are hard to justify months or years later.
                </p>
              </div>
            </div>

            <div
              className="section-cta reveal"
              style={{ transitionDelay: "380ms" }}
            >
              <a href="/solutions/incubator" className="cta-link">
                See how Incubators use Prufyy →
              </a>
            </div>
          </div>
        </section>

        {/* ======================================================
           ACCELERATOR PERSPECTIVE
           ====================================================== */}
        <section
          className="section-block offset-right reveal"
          style={{ transitionDelay: "0ms" }}
        >
          <div
            className="section-card reveal"
            style={{ transitionDelay: "120ms" }}
          >
            <span
              className="eyebrow reveal"
              style={{ transitionDelay: "160ms" }}
            >
              Accelerator perspective
            </span>

            <h2
              className="section-title reveal"
              style={{ transitionDelay: "200ms" }}
            >
              Speed matters.
              <br />
              Signal still matters more.
            </h2>

            <p className="reveal" style={{ transitionDelay: "260ms" }}>
              Accelerators operate under compressed timelines. When structure is
              missing, presentation quality replaces real diligence.
            </p>

            <ul
              className="bullet-list reveal"
              style={{ transitionDelay: "320ms" }}
            >
              <li>Application overload masks differentiation</li>
              <li>Mentor input becomes noisy and inconsistent</li>
              <li>Demo day replaces structured evaluation</li>
              <li>Credibility gaps appear post-program</li>
            </ul>

            <p
              className="muted reveal"
              style={{ transitionDelay: "360ms" }}
            >
              Prufyy enables fast programs without sacrificing judgment or
              clarity.
            </p>

            <div
              className="section-cta reveal"
              style={{ transitionDelay: "400ms" }}
            >
              <a href="/solutions/accelerator" className="cta-link">
                Explore Prufyy for Accelerators →
              </a>
            </div>
          </div>
        </section>

        {/* ======================================================
           ANGEL INVESTOR PERSPECTIVE
           ====================================================== */}
        <section
          className="section-block offset-left reveal"
          style={{ transitionDelay: "0ms" }}
        >
          <div
            className="section-card reveal"
            style={{ transitionDelay: "120ms" }}
          >
            <span
              className="eyebrow reveal"
              style={{ transitionDelay: "160ms" }}
            >
              Angel investor perspective
            </span>

            <h2
              className="section-title reveal"
              style={{ transitionDelay: "200ms" }}
            >
              Early-stage investing is not about certainty.
              <br />
              It is about honest signal.
            </h2>

            <p className="reveal" style={{ transitionDelay: "260ms" }}>
              Angel investors make decisions with limited data, personal capital,
              and incomplete visibility. The real risk is not intuition — it is
              assumption-driven conviction without structure.
            </p>

            <div
              className="card-grid two-col reveal"
              style={{ transitionDelay: "320ms" }}
            >
              <div className="info-card">
                <h4>Story over substance</h4>
                <p>
                  Charismatic storytelling can overpower evidence in early
                  conversations.
                </p>
              </div>

              <div className="info-card">
                <h4>Recycled proof</h4>
                <p>
                  The same proof is reused across unrelated claims without clear
                  boundaries.
                </p>
              </div>

              <div className="info-card">
                <h4>Social signaling</h4>
                <p>
                  External opinions quietly influence conviction and timing.
                </p>
              </div>

              <div className="info-card">
                <h4>Fading memory</h4>
                <p>
                  Early assumptions blur across multiple deals over time.
                </p>
              </div>
            </div>

            <p
              className="muted reveal"
              style={{ transitionDelay: "360ms" }}
            >
              Prufyy supports intuition with structure — without forcing
              rankings, scores, or consensus.
            </p>

            <div
              className="section-cta reveal"
              style={{ transitionDelay: "400ms" }}
            >
              <a href="/solutions/angel" className="cta-link">
                See how Angels evaluate with Prufyy →
              </a>
            </div>
          </div>
        </section>

        {/* ======================================================
           VENTURE CAPITAL / IC PERSPECTIVE
           ====================================================== */}
        <section
          className="section-block offset-right alt-bg reveal"
          style={{ transitionDelay: "0ms" }}
        >
          <div
            className="section-card wide reveal"
            style={{ transitionDelay: "120ms" }}
          >
            <span
              className="eyebrow reveal"
              style={{ transitionDelay: "160ms" }}
            >
              Venture capital perspective
            </span>

            <h2
              className="section-title reveal"
              style={{ transitionDelay: "200ms" }}
            >
              Venture decisions are institutional,
              <br />
              not intuitive
            </h2>

            <p className="reveal" style={{ transitionDelay: "260ms" }}>
              Venture capital decisions outlive individuals. Every memo, review,
              and investment committee discussion becomes part of an
              institutional record that must hold up over time.
            </p>

            <div
              className="card-grid two-col reveal"
              style={{ transitionDelay: "320ms" }}
            >
              <div className="info-card">
                <h4>Inconsistent memos</h4>
                <p>
                  Analysts evaluate deals with varying depth and structure,
                  weakening comparability.
                </p>
              </div>

              <div className="info-card">
                <h4>Detached evidence</h4>
                <p>
                  Proof is separated from claims, forcing partners to hunt for
                  context.
                </p>
              </div>

              <div className="info-card">
                <h4>Opinion leakage</h4>
                <p>
                  Early opinions influence later reviewers, reducing
                  independence.
                </p>
              </div>

              <div className="info-card">
                <h4>Lost rationale</h4>
                <p>
                  Decision logic fades months later, weakening institutional
                  learning.
                </p>
              </div>
            </div>

            <p
              className="muted reveal"
              style={{ transitionDelay: "360ms" }}
            >
              Prufyy brings claim-level structure, evidence traceability, and
              defensible judgment to IC workflows.
            </p>

            <div
              className="section-cta reveal"
              style={{ transitionDelay: "400ms" }}
            >
              <a href="/solutions/vc" className="cta-link">
                Explore Prufyy for Venture Firms →
              </a>
            </div>
          </div>
        </section>
        {/* END VC SECTION */}
        {/* ======================================================
           HOW PRUFYY WORKS (COMPACT, VISUAL)
           ====================================================== */}
        <section
          className="section-block offset-left reveal"
          style={{ transitionDelay: "0ms" }}
        >
          <div
            className="section-card reveal"
            style={{ transitionDelay: "120ms" }}
          >
            <span
              className="eyebrow reveal"
              style={{ transitionDelay: "160ms" }}
            >
              How Prufyy works
            </span>

            <h2
              className="section-title reveal"
              style={{ transitionDelay: "200ms" }}
            >
              From narrative to verifiable clarity
            </h2>

            <div
              className="card-grid three-col reveal"
              style={{ transitionDelay: "280ms" }}
            >
              <div className="info-card">
                <h4>Pitch in</h4>
                <p>
                  Founders bring their existing pitch. No rewriting. No forced
                  templates.
                </p>
              </div>

              <div className="info-card">
                <h4>Claims isolated</h4>
                <p>
                  Key market, traction, product, and team claims are separated
                  from narrative flow.
                </p>
              </div>

              <div className="info-card">
                <h4>Evidence attached</h4>
                <p>
                  Proof is linked only where credibility is questioned — not
                  everywhere.
                </p>
              </div>

              <div className="info-card">
                <h4>Shared intentionally</h4>
                <p>
                  One structured profile travels across evaluations, cohorts,
                  and conversations.
                </p>
              </div>

              <div className="info-card">
                <h4>Reviewed independently</h4>
                <p>
                  Evaluators draft privately. No peer influence. No signaling.
                </p>
              </div>

              <div className="info-card">
                <h4>Submitted with intent</h4>
                <p>
                  Final evaluations are locked, accountable, and reviewable.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* ======================================================
           TRUST, CONTROL & SAFETY
           ====================================================== */}
        <section
          className="section-block offset-right alt-bg reveal"
          style={{ transitionDelay: "0ms" }}
        >
          <div
            className="section-card reveal"
            style={{ transitionDelay: "120ms" }}
          >
            <span
              className="eyebrow reveal"
              style={{ transitionDelay: "160ms" }}
            >
              Trust & control
            </span>

            <h2
              className="section-title reveal"
              style={{ transitionDelay: "200ms" }}
            >
              Share without losing control
            </h2>

            <ul
              className="bullet-list reveal"
              style={{ transitionDelay: "260ms" }}
            >
              <li>Nothing is public by default</li>
              <li>Access is intentional and revocable</li>
              <li>Public links expire automatically</li>
              <li>No discovery, indexing, or exposure</li>
              <li>No implied endorsements or validation</li>
            </ul>

            <p
              className="muted reveal"
              style={{ transitionDelay: "320ms" }}
            >
              Prufyy protects both founders and institutions by design.
              Evaluation does not equal endorsement.
            </p>
          </div>
        </section>

        {/* ======================================================
           FINAL CTA
           ====================================================== */}
        <section
          className="section-block final-cta reveal"
          style={{ transitionDelay: "0ms" }}
        >
          <div
            className="final-card reveal"
            style={{ transitionDelay: "160ms" }}
          >
            <h2
              className="reveal"
              style={{ transitionDelay: "220ms" }}
            >
              Bring structure to how startups are evaluated
            </h2>

            <p
              className="reveal"
              style={{ transitionDelay: "280ms" }}
            >
              Prufyy helps founders and decision-makers focus on substance —
              not persuasion theatrics, assumptions, or noise.
            </p>

            <div
              className="final-cta-actions reveal"
              style={{ transitionDelay: "340ms" }}
            >
              <a href="/signup" className="btn-primary">
                Create your Prufyy profile
              </a>
              <a href="/pricing" className="btn-secondary">
                View pricing
              </a>
            </div>
          </div>
        </section>
      </div> {/* END home-container */}
      </main>

      <Footer />
    </>
  );
}

export default Home;
